import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, RefreshCw, Inbox, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/crm/EmptyState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Modal from '../../components/admin/Modal';
import AccountPicker from '../../components/admin/AccountPicker';
import SendAsPermissionsGrid from '../../components/admin/SendAsPermissionsGrid';
import Toast, { useToast } from '../../components/admin/Toast';
import NeedsReplyTab from './NeedsReplyTab';
import BroadcastsTab from './BroadcastsTab';
import TemplatesTab from './TemplatesTab';
import Outreach from './Outreach';
import {
  relativeTime, messageTime, shortDate, initials, firstName, avatarTone, shortName,
  CONTACT_ROLE_OPTIONS, GOOGLE_FLASH, NEEDS_REPLY_EVENT,
} from '../../lib/communications';
// Google's "G" mark, per their sign-in branding guidelines (white button,
// coloured G, "Sign in with Google").
function GoogleG() {
  return (
    <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48" className="shrink-0">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

const TABS = [
  ['needs-reply', 'Needs reply'],
  ['broadcasts', 'Broadcasts'],
  ['sequences', 'Sequences'],
  ['templates', 'Templates'],
  ['mailboxes', 'Mailboxes'],
  ['unmatched', 'Unmatched'],
];
const DEFAULT_TAB = 'needs-reply';

const ROLE_LABELS = { nexsav_admin: 'Admin', ops: 'Ops' };

// /admin/communications — Needs reply (default), Broadcasts (one email to
// many schools), Sequences (the multi-step campaigns), Templates, the
// connected Gmail mailboxes with the send-as permissions grid, and the
// emails the importer couldn't match to a school.
export default function Communications() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = TABS.some(([k]) => k === tabParam) ? tabParam : DEFAULT_TAB;

  const [flash, setFlash] = useState(null);
  const [toast, setToast] = useToast();
  const [unmatchedTotal, setUnmatchedTotal] = useState(null);
  const [needsReplyCounts, setNeedsReplyCounts] = useState(null); // { total, mine }

  // ?google=<status> lands here after the OAuth round-trip. Show once,
  // then scrub it from the URL so refresh/back doesn't repeat it.
  const google = searchParams.get('google');
  useEffect(() => {
    if (!google) return;
    setFlash(GOOGLE_FLASH[google] || { tone: 'grey', text: `Google sign-in finished with status “${google}”.` });
    const sp = new URLSearchParams(searchParams);
    sp.delete('google');
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [google]);

  const setTab = (key) => {
    const sp = new URLSearchParams(searchParams);
    if (key === DEFAULT_TAB) sp.delete('tab'); else sp.set('tab', key);
    setSearchParams(sp, { replace: true });
  };

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState title="Staff only" description="Communications is restricted to NexSav staff." />;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Communications</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Everything a school hears from us: who’s waiting on a reply, broadcasts and sequences, templates, mailboxes.</p>
        </div>
      </div>

      {flash && (
        <FlashBanner tone={flash.tone} onClose={() => setFlash(null)}>{flash.text}</FlashBanner>
      )}

      <div className="flex flex-wrap items-center gap-1 mb-6 border-b border-white/[0.05]" role="tablist" aria-label="Communications sections">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 -mb-px border-b-2 text-[13px] transition-colors inline-flex items-center gap-2 ${
              tab === key ? 'border-[#B0FC38] text-white' : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {label}
            {key === 'unmatched' && unmatchedTotal != null && unmatchedTotal > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 tabular-nums" data-testid="unmatched-count">{unmatchedTotal}</span>
            )}
            {key === 'needs-reply' && needsReplyCounts != null && needsReplyCounts.total > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 tabular-nums" data-testid="needs-reply-count">{needsReplyCounts.total}</span>
            )}
          </button>
        ))}
      </div>

      <UnmatchedCountProbe onCount={setUnmatchedTotal} active={tab !== 'unmatched'} />
      <NeedsReplyCountProbe onCounts={setNeedsReplyCounts} active={tab !== 'needs-reply'} />

      {tab === 'needs-reply' && <NeedsReplyTab user={user} onToast={setToast} onCounts={setNeedsReplyCounts} counts={needsReplyCounts} />}
      {tab === 'broadcasts' && <BroadcastsTab onToast={setToast} />}
      {tab === 'sequences' && <Outreach embedded />}
      {tab === 'templates' && <TemplatesTab onToast={setToast} />}
      {tab === 'mailboxes' && <MailboxesTab user={user} onToast={setToast} />}
      {tab === 'unmatched' && <UnmatchedTab onToast={setToast} onTotal={setUnmatchedTotal} user={user} />}

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}

// Needs-reply counts for the tab badge while that tab isn't mounted (it
// reports its own). Also re-fetched when a reply/resolve happens elsewhere.
function NeedsReplyCountProbe({ onCounts, active }) {
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    const run = () => {
      const p = api.get('/api/admin/communications/needs-reply?count=1');
      if (!p || typeof p.then !== 'function') return;
      p.then((d) => { if (alive) onCounts({ total: Number(d?.total) || 0, mine: Number(d?.mine) || 0 }); }).catch(() => {});
    };
    run();
    window.addEventListener(NEEDS_REPLY_EVENT, run);
    return () => { alive = false; window.removeEventListener(NEEDS_REPLY_EVENT, run); };
  }, [active, onCounts]);
  return null;
}

// Fetches the unmatched count for the tab badge while the Unmatched tab
// itself isn't mounted (it reports its own total when it is).
function UnmatchedCountProbe({ onCount, active }) {
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    const p = api.get('/api/admin/communications/unmatched?limit=1');
    if (!p || typeof p.then !== 'function') return undefined;
    p.then((d) => { if (alive) onCount(Number(d?.total) || 0); }).catch(() => {});
    return () => { alive = false; };
  }, [active, onCount]);
  return null;
}

function FlashBanner({ tone, children, onClose }) {
  const tones = {
    lime: 'bg-[#B0FC38]/[0.08] border-[#B0FC38]/25 text-[#d9ffa3]',
    amber: 'bg-amber-500/[0.08] border-amber-500/25 text-amber-200',
    red: 'bg-red-500/10 border-red-500/20 text-red-300',
    grey: 'bg-white/[0.03] border-white/[0.08] text-gray-300',
  };
  return (
    <div role="status" className={`mb-4 flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-[12px] ${tones[tone] || tones.grey}`} data-testid="flash-banner">
      <span>{children}</span>
      <button type="button" onClick={onClose} aria-label="Dismiss" className="p-0.5 rounded text-current opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

// The overnight backstop lives in the cron-worker (mailbox sync + verifying
// stuck sends while nobody has the portal open). "Run now" proves it can
// reach Gmail from there; page loads do the same work the rest of the day.
function BackstopCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, oauthConfigured, mailboxes, sendJobs, error } | { failed: message }
  const run = async () => {
    setBusy(true); setResult(null);
    try { setResult(await api.post('/api/admin/communications/mailboxes/backstop', {})); }
    catch (err) { setResult({ failed: err?.data?.error || err?.message || 'The backstop did not run' }); }
    finally { setBusy(false); }
  };
  let verdict = null;
  if (result?.failed) verdict = <span className="text-red-300">{result.failed}</span>;
  else if (result && !result.oauthConfigured) verdict = <span className="text-amber-300">The worker has no working Google credentials — the overnight pass skips. Page loads still sync.</span>;
  else if (result?.error) verdict = <span className="text-red-300">Ran, but failed: {result.error}</span>;
  else if (result) {
    const synced = result.mailboxes.filter((m) => m.ok);
    const failed = result.mailboxes.filter((m) => !m.ok);
    const imported = synced.reduce((n, m) => n + (m.imported || 0), 0);
    verdict = (
      <span className="text-[#B0FC38]">
        Worker has Google access. Synced {synced.length} mailbox{synced.length === 1 ? '' : 'es'} ({imported} new email{imported === 1 ? '' : 's'}){failed.length ? <span className="text-amber-300"> · {failed.length} could not sync ({failed.map((m) => m.reason).filter(Boolean).join(', ') || 'see mailbox card'})</span> : ''}{result.sendJobs.length ? ` · ${result.sendJobs.length} pending send${result.sendJobs.length === 1 ? '' : 's'} checked` : ''}.
      </span>
    );
  }
  return (
    <div className="mt-5 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="backstop-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Overnight backstop</div>
          <p className="text-[12.5px] text-gray-400 max-w-[64ch]">Every night the cron-worker re-syncs mailboxes and verifies any send that timed out, so nothing depends on someone having the portal open. Run it now to check it can reach Gmail.</p>
        </div>
        <button type="button" onClick={run} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] border border-white/[0.1] text-gray-200 hover:text-white hover:border-white/[0.2] disabled:opacity-50" data-testid="backstop-run">
          <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} aria-hidden="true" /> {busy ? 'Running…' : 'Run the overnight backstop now'}
        </button>
      </div>
      {verdict && <div className="mt-3 text-[12.5px]" role="status" data-testid="backstop-result">{verdict}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    connected: ['Connected', 'border-[#B0FC38]/35 bg-[#B0FC38]/[0.12] text-[#B0FC38]'],
    none: ['Not connected', 'border-amber-500/35 bg-amber-500/[0.12] text-amber-300'],
    error: ['Connection problem', 'border-red-500/35 bg-red-500/[0.12] text-red-300'],
    disconnected: ['Disconnected', 'border-white/[0.08] text-gray-400'],
  };
  const [label, cls] = map[status] || map.none;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider whitespace-nowrap ${cls}`}>{label}</span>;
}

// ---------------------------------------------------------------------------

function MailboxesTab({ user, onToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState({}); // mailboxId → 'sync' | 'aliases' | 'connect'
  const [pendingDisconnect, setPendingDisconnect] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) { setLoading(true); setError(''); }
    try {
      const res = await api.get('/api/admin/communications/mailboxes');
      setData(res || null);
    } catch (err) {
      if (!quiet) setError(err?.message || 'Failed to load mailboxes');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // While any mailbox is still importing, poll quietly. Each poll also nudges
  // the import forward server-side, so a dropped chunk hand-off self-heals as
  // long as someone has this page open.
  const importing = !!data?.staff?.some((s) => s.mailbox && s.mailbox.status === 'connected' && !s.mailbox.backfillDone);
  useEffect(() => {
    if (!importing) return undefined;
    const t = setInterval(() => { load({ quiet: true }); }, 15000);
    return () => clearInterval(t);
  }, [importing, load]);

  const patchMailbox = (userId, mailbox) => setData((d) => d ? ({
    ...d,
    staff: d.staff.map((s) => (s.id === userId ? { ...s, mailbox } : s)),
  }) : d);

  const connect = async () => {
    setBusy((b) => ({ ...b, connect: true }));
    try {
      const res = await api.get('/api/auth/google/start?scope=gmail&return_to=/admin/communications');
      if (res?.authUrl) window.location.assign(res.authUrl);
      else throw new Error('Google sign-in did not return a URL');
    } catch (err) {
      onToast({ message: 'Couldn’t start Google sign-in', detail: err?.message });
      setBusy((b) => ({ ...b, connect: false }));
    }
  };

  const syncNow = async (s) => {
    setBusy((b) => ({ ...b, [s.mailbox.id]: 'sync' }));
    try {
      const res = await api.post(`/api/admin/communications/mailboxes/${s.mailbox.id}/sync`, {});
      if (res?.mailbox) patchMailbox(s.id, res.mailbox);
      const r = res?.result || {};
      onToast({
        message: r.ok === false ? 'Sync didn’t run' : `Synced ${firstName(s.name)}’s mailbox`,
        detail: r.ok === false ? (r.reason || '') : `${r.imported ?? 0} imported · ${r.inspected ?? 0} inspected${r.more ? ' · more to come' : ''}`,
      });
    } catch (err) {
      onToast({ message: 'Sync failed', detail: err?.message });
    } finally {
      setBusy((b) => ({ ...b, [s.mailbox.id]: null }));
    }
  };

  const refreshAliases = async (s) => {
    setBusy((b) => ({ ...b, [s.mailbox.id]: 'aliases' }));
    try {
      const res = await api.post(`/api/admin/communications/mailboxes/${s.mailbox.id}/refresh-aliases`, {});
      if (res?.mailbox) patchMailbox(s.id, res.mailbox);
      onToast({ message: 'Aliases refreshed', detail: `${(res?.mailbox?.sendAs || []).length} send-as address${(res?.mailbox?.sendAs || []).length === 1 ? '' : 'es'}` });
    } catch (err) {
      onToast({ message: 'Couldn’t refresh aliases', detail: err?.message });
    } finally {
      setBusy((b) => ({ ...b, [s.mailbox.id]: null }));
    }
  };

  const isAdmin = user?.role === 'nexsav_admin';

  return (
    <div>
      <p className="text-[13px] text-gray-400 mb-4">Each person connects their own @nexsav.net Gmail once. Aliases come along automatically.</p>

      {data && data.oauthConfigured === false && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/25 text-[12px] text-amber-200" role="status">
          Google sign-in isn’t configured on this environment.
        </div>
      )}
      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">{error}</div>}

      {loading && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 animate-pulse">
          {[0, 1, 2].map((i) => <div key={i} className="h-44 rounded-xl bg-white/[0.03]" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-5">
          {(data?.staff || []).map((s) => (
            <MailboxCard
              key={s.id}
              staff={s}
              isAdmin={isAdmin}
              oauthConfigured={data?.oauthConfigured !== false}
              busy={s.mailbox ? busy[s.mailbox.id] : (busy.connect ? 'connect' : null)}
              onConnect={connect}
              onSync={() => syncNow(s)}
              onRefreshAliases={() => refreshAliases(s)}
              onDisconnect={() => setPendingDisconnect(s)}
            />
          ))}
          {data && (data.staff || []).length === 0 && (
            <div className="col-span-full"><EmptyState icon={Mail} title="No staff accounts" description="Admin and ops users appear here once they exist." /></div>
          )}
        </div>
      )}

      {data && <SendAsPermissionsGrid isAdmin={isAdmin} onToast={onToast} />}

      {data && <BackstopCard />}

      {data?.importRule && (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3.5">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2">What gets imported</div>
          <RuleLine text={data.importRule.linked} verdict="Linked to that school" tone="text-[#B0FC38]" />
          <RuleLine text={data.importRule.unmatched} verdict="Unmatched · you assign" tone="text-amber-300" />
          <RuleLine text={data.importRule.skipped} verdict="Not imported" tone="text-gray-400" last />
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingDisconnect)}
        onClose={() => setPendingDisconnect(null)}
        title={`Disconnect ${pendingDisconnect?.mailbox?.email || 'this mailbox'}?`}
        description="Emails already imported stay. New mail stops syncing until you reconnect."
        confirmLabel="Disconnect"
        destructive
        onConfirm={async () => {
          const s = pendingDisconnect;
          const res = await api.delete(`/api/admin/communications/mailboxes/${s.mailbox.id}`);
          patchMailbox(s.id, res?.mailbox || { ...s.mailbox, status: 'disconnected' });
          onToast({ message: `Disconnected ${s.mailbox.email}` });
        }}
      />
    </div>
  );
}

function RuleLine({ text, verdict, tone, last = false }) {
  return (
    <div className={`flex items-start justify-between gap-4 py-1.5 text-[12.5px] ${last ? '' : 'border-b border-white/[0.05]'}`}>
      <span className="text-gray-400">{text}</span>
      <span className={`whitespace-nowrap ${tone}`}>{verdict}</span>
    </div>
  );
}

function MailboxCard({ staff: s, isAdmin, oauthConfigured, busy, onConnect, onSync, onRefreshAliases, onDisconnect }) {
  const mb = s.mailbox;
  const status = mb ? mb.status : 'none';
  const connected = status === 'connected' || status === 'error';
  const first = firstName(s.name) || s.name;
  const ghostBtn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className={`rounded-xl border bg-white/[0.02] p-4 flex flex-col ${mb ? 'border-white/[0.06]' : 'border-dashed border-white/[0.12]'}`} data-testid={`mailbox-card-${s.id}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span aria-hidden="true" className={`w-7 h-7 rounded-full inline-grid place-items-center text-[11px] font-semibold shrink-0 ${avatarTone(s.name)}`}>{initials(s.name)}</span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-white truncate">{s.name}{s.isMe && <span className="text-gray-500 font-normal"> · you</span>}</div>
            <div className="text-[11.5px] text-gray-500 truncate">{mb?.email || s.email} · {ROLE_LABELS[s.role] || s.role}</div>
          </div>
        </div>
        <StatusPill status={status} />
      </div>

      {connected && (
        <>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Sends as</div>
          <ul className="list-none m-0 p-0 my-2 space-y-1">
            {(mb.sendAs && mb.sendAs.length > 0 ? mb.sendAs : [{ email: mb.email, name: mb.displayName, is_default: true, verified: true }]).map((a) => (
              <li key={a.email} className="text-[12px]">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-gray-200 truncate">{a.name || a.email}</span>
                  <span className="text-gray-500 tabular-nums whitespace-nowrap">{a.email}{a.verified === false ? ' · unverified' : ''}{a.hasSignature === false ? ' · no signature' : ''}</span>
                </div>
                {a.signatureIssue === 'gmail_only_image' && (
                  <div className="mt-0.5 text-[11px] text-amber-300" data-testid="signature-issue">
                    The image in this signature is stored inside Gmail and only shows for you. In Gmail → Settings → Signature, remove it and insert the logo as a <b>web address</b> (<span className="font-mono">https://nexsav.net/email/nexsav-logo.png</span>) instead, then press Aliases here.
                  </div>
                )}
                {a.signatureIssue === 'hashed_asset' && (
                  <div className="mt-0.5 text-[11px] text-amber-300" data-testid="signature-issue">
                    The logo in this signature points at a nexsav.net address that changes with every deploy, so it breaks silently. In Gmail → Settings → Signature, point the image at <span className="font-mono">https://nexsav.net/email/nexsav-logo.png</span> (dark, for white backgrounds; <span className="font-mono">nexsav-logo-white.png</span> for dark ones), then press Aliases here.
                  </div>
                )}
              </li>
            ))}
          </ul>
          <KV label="Last synced">
            {mb.syncing ? (
              <span className="text-amber-300">Syncing…</span>
            ) : !mb.backfillDone ? (
              <span className="text-amber-300">Importing… {mb.importedCount ?? 0} so far</span>
            ) : (
              <span className="tabular-nums">{mb.lastSyncedAt ? relativeTime(mb.lastSyncedAt) : 'Not yet'}</span>
            )}
          </KV>
          <KV label="History imported" last>
            <span>{mb.backfillAfter ? `since ${shortDate(mb.backfillAfter)}` : 'all history'} · {mb.importedCount ?? 0} email{Number(mb.importedCount) === 1 ? '' : 's'}</span>
          </KV>
          <p className="mt-2 text-[11px] text-gray-600 leading-relaxed">
            “Sends as” is what shows up in the From picker when anyone composes or replies. Who may pick {s.isMe ? 'your' : `${first}’s`} mailbox is set in the grid below.
          </p>
          {mb.lastError && (
            <div className="mt-2 text-[11.5px] text-red-300 break-words" data-testid="mailbox-error">{mb.lastError}</div>
          )}
        </>
      )}

      {status === 'disconnected' && (
        <p className="text-[12px] text-gray-500 mt-1 mb-2">
          Disconnected{mb.disconnectedAt ? ` ${relativeTime(mb.disconnectedAt)}` : ''}. Imported emails are kept; nothing new syncs until {s.isMe ? 'you reconnect' : `${first} reconnects`}.
        </p>
      )}

      {!mb && !s.isMe && (
        <p className="text-[12px] text-gray-500 mt-1 mb-2">
          {first}’s emails with schools aren’t visible yet. Ask {first} to connect.
        </p>
      )}
      {!mb && s.isMe && (
        <p className="text-[12px] text-gray-500 mt-1 mb-2">
          Connect once. Emails with school contacts are imported and kept in sync; nothing internal or personal is.
        </p>
      )}

      <div className="mt-auto pt-3 flex items-center gap-1 flex-wrap">
        {/* Not connected, or connected-but-broken (expired / undecryptable
            grant): the owner reconnects in place. No need to Disconnect first. */}
        {s.isMe && (!connected || status === 'error') && (
          <button
            type="button"
            onClick={onConnect}
            disabled={!oauthConfigured || busy === 'connect'}
            data-testid="connect-gmail"
            className="inline-flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full text-[13px] font-medium bg-white text-[#1f1f1f] hover:bg-[#f2f2f2] border border-[#dadce0] shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <GoogleG />
            {busy === 'connect' ? 'Opening Google…' : status === 'disconnected' || status === 'error' ? 'Reconnect with Google' : 'Sign in with Google'}
          </button>
        )}
        {s.isMe && connected && (
          <>
            <button type="button" onClick={onSync} disabled={!!busy} className={`${ghostBtn} px-2`}>
              <RefreshCw className={`w-3 h-3 ${busy === 'sync' ? 'animate-spin' : ''}`} /> {busy === 'sync' ? 'Syncing…' : 'Sync now'}
            </button>
            <button type="button" onClick={onRefreshAliases} disabled={!!busy} className={`${ghostBtn} px-2`}>
              {busy === 'aliases' ? 'Refreshing…' : 'Aliases'}
            </button>
            {status === 'connected' && (
              <button type="button" onClick={onConnect} disabled={!!busy || !oauthConfigured} className={`${ghostBtn} px-2`} data-testid="reconnect-gmail" title="Sign in with Google again — for example after a key rotation">
                {busy === 'connect' ? 'Opening Google…' : 'Reconnect'}
              </button>
            )}
          </>
        )}
        {connected && (s.isMe || isAdmin) && (
          <button type="button" onClick={onDisconnect} disabled={!!busy} className={`${ghostBtn} px-2 text-red-300/80 hover:text-red-300 ml-auto`}>
            Disconnect
          </button>
        )}
        {!s.isMe && !connected && (
          <span className="text-[12px] text-gray-600">Ask {first} to connect</span>
        )}
      </div>
    </div>
  );
}

function KV({ label, children, last = false }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-1.5 text-[12.5px] ${last ? '' : 'border-b border-white/[0.05]'}`}>
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-200 text-right">{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------

const UNMATCHED_VIEW_KEY = 'nexsav.communications.unmatchedView';

function UnmatchedTab({ onToast, onTotal, user }) {
  // "By sender" groups the bucket by the school-side person (one decision files all their
  // threads); "By email" is the flat thread list. The choice sticks per browser.
  const [view, setViewState] = useState(() => { try { return localStorage.getItem(UNMATCHED_VIEW_KEY) === 'emails' ? 'emails' : 'senders'; } catch { return 'senders'; } });
  const setView = (v) => { setViewState(v); try { localStorage.setItem(UNMATCHED_VIEW_KEY, v); } catch { /* ignore */ } };
  const [rows, setRows] = useState([]);
  const [senders, setSenders] = useState([]);
  const [senderTotals, setSenderTotals] = useState({ people: 0, threads: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filing, setFiling] = useState(null);
  const [dismissing, setDismissing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (view === 'senders') {
        const res = await api.get('/api/admin/communications/unmatched/senders');
        setSenders(res?.senders || []);
        setSenderTotals({ people: Number(res?.total) || (res?.senders || []).length, threads: Number(res?.totalConversations) || 0 });
        onTotal(Number(res?.totalConversations) || 0);
      } else {
        const res = await api.get('/api/admin/communications/unmatched?limit=50');
        setRows(res?.conversations || []);
        onTotal(Number(res?.total) || (res?.conversations || []).length);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load unmatched conversations');
    } finally {
      setLoading(false);
    }
  }, [onTotal, view]);
  useEffect(() => { load(); }, [load]);

  // One click on a suggested school: file the sender's newest thread under it and save them as a
  // contact, which files every other thread they are on server-side.
  const [quickFiling, setQuickFiling] = useState(null); // sender email
  const fileSender = async (sender, suggestion) => {
    setQuickFiling(sender.email);
    try {
      const role = suggestion.role || roleForSuggestion(suggestion.reason);
      const res = await api.post(`/api/admin/communications/conversations/${sender.conversation.id}/link`, {
        account_id: suggestion.accountId,
        create_contact: { email: sender.email, name: sender.name || undefined, role },
      });
      onLinked(sender.conversation, res, sender.email, sender);
    } catch (err) {
      onToast({ message: 'Filing failed', detail: err?.message });
    } finally {
      setQuickFiling(null);
    }
  };

  // Filing with "save as contact" also files every other Unmatched thread from
  // that address server-side, so refetch rather than dropping one row.
  const onLinked = (conv, res, contactEmail, sender = null) => {
    const account = res?.account;
    const also = Number(res?.alsoLinked) || 0;
    setFiling(null);
    const who = sender ? (sender.name || sender.email) : null;
    onToast({
      message: who
        ? `Filed ${who} under ${account?.name || 'the school'}${also > 0 ? ` — ${also + 1} threads` : ''}`
        : `Filed under ${account?.name || 'the school'}${also > 0 ? ` and ${also} more from ${contactEmail || 'that address'}` : ''}`,
      detail: res?.contactId ? 'Contact saved — future mail links automatically.' : undefined,
    });
    load();
  };

  const onDismissed = (conv, res) => {
    const also = Number(res?.alsoDismissed) || 0;
    setDismissing(null);
    onToast({
      message: `Dismissed${also > 0 ? ` and ${also} more from that address` : ''}`,
      detail: (res?.ignoredAddresses || []).length ? `Ignoring ${res.ignoredAddresses.join(', ')} from now on.` : undefined,
      ms: 6500,
      action: {
        label: 'Undo',
        onClick: async () => {
          try {
            await api.delete(`/api/admin/communications/conversations/${conv.id}/dismiss`);
            onToast({ message: 'Dismissal undone', detail: 'The conversation is back in Unmatched. Ignored addresses stay ignored.' });
            load();
          } catch (err) {
            onToast({ message: 'Couldn’t undo', detail: err?.message });
          }
        },
      },
    });
    load();
  };

  return (
    <div>
      <p className="text-[13px] text-gray-400 mb-1">Emails from a school address we don’t have as a contact. File each one under its school; save the sender as a contact and future mail links itself.{user?.role === 'ops' && <span className="block text-[12px] text-gray-500 mt-1" data-testid="scope-note">You see the stray emails that landed in your own mailbox. Admins see everyone’s.</span>}</p>
      <p className="text-[12px] text-gray-500 mb-4">Dismiss emails that aren’t about a school (district office invites, vendors). Ignored addresses stop appearing here.</p>
      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">{error}</div>}

      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="inline-flex rounded-lg border border-white/[0.08] bg-white/[0.02] p-0.5" role="group" aria-label="Unmatched view">
          {[['senders', 'By sender'], ['emails', 'By email']].map(([k, label]) => (
            <button
              key={k}
              type="button"
              aria-pressed={view === k}
              onClick={() => setView(k)}
              className={`px-3 py-1 rounded-md text-[12px] transition-colors ${view === k ? 'bg-white/[0.08] text-white' : 'text-gray-400 hover:text-gray-200'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {view === 'senders' && !loading && senderTotals.people > 0 && (
          <div className="text-[12px] text-gray-500 tabular-nums" data-testid="sender-totals">
            {senderTotals.people} {senderTotals.people === 1 ? 'person' : 'people'} · {senderTotals.threads} {senderTotals.threads === 1 ? 'thread' : 'threads'}. File a person once and every thread they are on is filed.
          </div>
        )}
      </div>

      {view === 'senders' ? (
        loading && senders.length === 0 ? (
          <div className="space-y-2 animate-pulse">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-white/[0.03]" />)}</div>
        ) : senders.length === 0 ? (
          <EmptyState icon={Inbox} title="Nothing to file — every imported email is linked to a school." />
        ) : (
          <ul className="space-y-2 list-none m-0 p-0">
            {senders.map((sd) => (
              <SenderRow
                key={sd.email}
                sender={sd}
                busy={quickFiling === sd.email}
                onQuickFile={(sg) => fileSender(sd, sg)}
                onFile={(sg) => setFiling({ ...sd.conversation, __sender: sd, __suggestion: sg || null })}
                onDismiss={() => setDismissing({ ...sd.conversation, __sender: sd })}
              />
            ))}
          </ul>
        )
      ) : loading && rows.length === 0 ? (
        <div className="space-y-2 animate-pulse">{[0, 1].map((i) => <div key={i} className="h-16 rounded-xl bg-white/[0.03]" />)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Inbox} title="Nothing to file — every imported email is linked to a school." />
      ) : (
        <ul className="space-y-2 list-none m-0 p-0">
          {rows.map((c) => {
            const msgs = c.messages || [];
            const last = msgs[msgs.length - 1];
            return (
              <li key={c.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-3 flex items-start gap-3" data-testid="unmatched-row">
                <span aria-hidden="true" className={`w-7 h-7 rounded-full inline-grid place-items-center text-[11px] font-semibold shrink-0 ${avatarTone((c.externalEmails || [])[0] || c.subject)}`}>
                  {initials(last?.from?.name || (c.externalEmails || [])[0] || c.subject)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-white truncate">{c.subject || '(no subject)'}</div>
                  <div className="text-[11.5px] text-gray-500 truncate mt-0.5">{(c.externalEmails || []).join(', ') || 'No external addresses'}</div>
                  {last?.snippet && (
                    <div className="text-[12px] text-gray-400 mt-1 line-clamp-2">
                      {last.from?.name ? <span className="text-gray-300">{shortName(last.from.name, last.from.email)}: </span> : null}{last.snippet}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className="text-[11px] text-gray-500 tabular-nums whitespace-nowrap">{messageTime(c.lastMessageAt)}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setDismissing(c)}
                      className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-[12px] text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
                    >
                      Dismiss
                    </button>
                    <button
                      type="button"
                      onClick={() => setFiling(c)}
                      className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-[12px] border border-white/[0.08] bg-white/[0.02] text-gray-200 hover:text-white hover:border-white/[0.15] transition-colors"
                    >
                      File under school…
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {filing && (
        <FileUnderSchoolModal
          conversation={filing}
          onClose={() => setFiling(null)}
          onLinked={(conv, res, email) => onLinked(conv, res, email, filing.__sender || null)}
          initialAccountId={filing.__suggestion?.accountId || ''}
          initialRole={filing.__suggestion ? (filing.__suggestion.role || roleForSuggestion(filing.__suggestion.reason)) : undefined}
          initialName={filing.__sender?.name || undefined}
        />
      )}
      {dismissing && (
        <DismissModal conversation={dismissing} onClose={() => setDismissing(null)} onDismissed={onDismissed} />
      )}
    </div>
  );
}

// Which contact role a one-click filing records, from the suggestion's reason.
function roleForSuggestion(reason) {
  if (/^Principal of record/.test(reason || '')) return 'principal';
  if (/^Superintendent of record/.test(reason || '')) return 'superintendent';
  return 'other_school';
}

const CONFIDENCE_DOT = { high: 'bg-[#B0FC38]', medium: 'bg-amber-300', low: 'bg-gray-500' };
const CONFIDENCE_LABEL = { high: 'Strong match', medium: 'Likely match', low: 'Possible match' };

// One school-side person and everything they have written to us that is not filed yet.
// A strong or likely suggestion files in one click; a possible one opens the picker with the
// school pre-selected so a human confirms first.
function SenderRow({ sender: sd, busy, onQuickFile, onFile, onDismiss }) {
  const who = sd.name || sd.email.split('@')[0];
  const suggestions = sd.suggestions || [];
  return (
    <li className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-3 flex items-start gap-3" data-testid="sender-row">
      <span aria-hidden="true" className={`w-8 h-8 rounded-full inline-grid place-items-center text-[11px] font-semibold shrink-0 ${avatarTone(sd.email)}`}>
        {initials(sd.name || sd.email)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-white truncate">{who}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/[0.06] text-gray-300 tabular-nums shrink-0" data-testid="sender-count">{sd.count} {sd.count === 1 ? 'thread' : 'threads'}</span>
        </div>
        <div className="text-[11.5px] text-gray-500 truncate mt-0.5">{sd.email}</div>
        {sd.latest?.subject && (
          <div className="text-[12px] text-gray-400 mt-1 line-clamp-2">
            <span className="text-gray-300">{sd.latest.subject}</span>{sd.latest.snippet ? <span> — {sd.latest.snippet}</span> : null}
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-2">
          {suggestions.map((sg) => {
            const oneClick = sg.confidence !== 'low';
            return (
              <button
                key={sg.accountId}
                type="button"
                disabled={busy}
                onClick={() => (oneClick ? onQuickFile(sg) : onFile(sg))}
                title={`${CONFIDENCE_LABEL[sg.confidence] || 'Match'}: ${sg.reason}${oneClick ? '' : ' — opens the picker to confirm'}`}
                data-testid="suggestion-chip"
                data-confidence={sg.confidence}
                className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-lg text-[12px] border border-white/[0.08] bg-white/[0.02] text-gray-200 hover:text-white hover:border-[#B0FC38]/40 disabled:opacity-50 transition-colors max-w-full"
              >
                <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full shrink-0 ${CONFIDENCE_DOT[sg.confidence] || CONFIDENCE_DOT.low}`} />
                <span className="truncate">{oneClick ? 'File under ' : 'Maybe '}<span className="font-medium">{sg.name}</span></span>
                <span className="text-gray-500 truncate hidden sm:inline">· {sg.reason}</span>
              </button>
            );
          })}
          {suggestions.length === 0 && sd.hints?.district && (
            <span className="text-[12px] text-gray-500" data-testid="district-hint">Looks like a District {sd.hints.district} office — no school to file under.</span>
          )}
          {suggestions.length === 0 && !sd.hints?.district && (
            <span className="text-[12px] text-gray-600">No school named in these emails.</span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 shrink-0">
        <span className="text-[11px] text-gray-500 tabular-nums whitespace-nowrap">{messageTime(sd.lastMessageAt)}</span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onDismiss}
            disabled={busy}
            className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-[12px] text-gray-400 hover:text-white hover:bg-white/[0.04] disabled:opacity-50 transition-colors"
          >
            Dismiss…
          </button>
          <button
            type="button"
            onClick={() => onFile(null)}
            disabled={busy}
            className="inline-flex items-center px-2.5 py-1.5 rounded-lg text-[12px] border border-white/[0.08] bg-white/[0.02] text-gray-200 hover:text-white hover:border-white/[0.15] disabled:opacity-50 transition-colors"
          >
            {busy ? 'Filing…' : suggestions.length ? 'Other school…' : 'File under school…'}
          </button>
        </div>
      </div>
    </li>
  );
}

const inputCls = 'w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white placeholder-gray-500 focus:outline-none focus:border-[#B0FC38]/40';

export function FileUnderSchoolModal({ conversation: c, onClose, onLinked, initialAccountId = '', initialRole, initialName }) {
  const emails = c.externalEmails || [];
  const lastFrom = [...(c.messages || [])].reverse().find((m) => m.direction === 'inbound')?.from;
  const [accountId, setAccountId] = useState(initialAccountId || '');
  const [saveContact, setSaveContact] = useState(emails.length > 0);
  const [email, setEmail] = useState(emails.includes(lastFrom?.email) ? lastFrom.email : (emails[0] || ''));
  const [name, setName] = useState(initialName || (lastFrom?.email === email ? (lastFrom?.name || '') : ''));
  const [role, setRole] = useState(initialRole || 'other_school');
  const [roleTitle, setRoleTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!accountId) { setError('Choose a school first.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const body = { account_id: accountId };
      if (saveContact && email) body.create_contact = { email, name: name.trim() || undefined, role, ...(role.startsWith('other_') && roleTitle.trim() ? { role_title: roleTitle.trim() } : {}) };
      const res = await api.post(`/api/admin/communications/conversations/${c.id}/link`, body);
      onLinked(c, res, saveContact ? email : null);
    } catch (err) {
      setError(err?.message || 'Filing failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="File under school"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !accountId}
            className="px-4 py-1.5 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Filing…' : 'File conversation'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="text-[12px] text-gray-500">
          <span className="text-gray-300 font-medium">{c.subject || '(no subject)'}</span>
          {emails.length > 0 && <span> · {emails.join(', ')}</span>}
        </div>
        {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}

        <div>
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">School</div>
          <AccountPicker value={accountId} onChange={setAccountId} required placeholder="Search schools by name, slug, or DBN…" />
        </div>

        {emails.length > 0 && (
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] p-3 space-y-3">
            <label className="inline-flex items-center gap-2 text-[12px] text-gray-300 select-none cursor-pointer">
              <input type="checkbox" checked={saveContact} onChange={(e) => setSaveContact(e.target.checked)} className="accent-[#B0FC38]" />
              Also save {emails.length === 1 ? <span className="text-white">{emails[0]}</span> : 'the sender'} as a contact
            </label>
            {saveContact && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {emails.length > 1 && (
                  <label className="block md:col-span-2">
                    <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Address</div>
                    <select className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)}>
                      {emails.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </label>
                )}
                <label className="block">
                  <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Name</div>
                  <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
                </label>
                <label className="block">
                  <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Role</div>
                  <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value)}>
                    {CONTACT_ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </label>
                {role.startsWith('other_') && (
                  <label className="block">
                    <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Exact role <span className="normal-case tracking-normal text-gray-600">(optional)</span></div>
                    <input className={inputCls} value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} placeholder="e.g. School Business Manager" maxLength={80} data-testid="file-role-title" />
                  </label>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// "Not a school email?" — dismiss an Unmatched conversation and optionally
// remember its addresses so future mail from them never lands here.
export function DismissModal({ conversation: c, onClose, onDismissed }) {
  const emails = c.externalEmails || [];
  const [ignore, setIgnore] = useState(() => new Set(emails));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const toggle = (e) => setIgnore((prev) => {
    const next = new Set(prev);
    if (next.has(e)) next.delete(e); else next.add(e);
    return next;
  });

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const body = { ignore_addresses: emails.filter((e) => ignore.has(e)) };
      if (reason.trim()) body.reason = reason.trim();
      const res = await api.post(`/api/admin/communications/conversations/${c.id}/dismiss`, body);
      onDismissed(c, res);
    } catch (err) {
      setError(err?.message || 'Dismiss failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Not a school email?"
      maxWidth="max-w-md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="dismiss-confirm"
            className="px-4 py-1.5 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Dismissing…' : 'Dismiss'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="text-[12px] text-gray-500">
          <span className="text-gray-300 font-medium">{c.subject || '(no subject)'}</span> leaves Unmatched. The emails are kept; nothing is deleted.
        </div>
        {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}

        {emails.length > 0 && (
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] p-3 space-y-2">
            {emails.map((e) => (
              <label key={e} className="flex items-center gap-2 text-[12px] text-gray-300 select-none cursor-pointer">
                <input type="checkbox" checked={ignore.has(e)} onChange={() => toggle(e)} className="accent-[#B0FC38]" />
                Also ignore <span className="text-white">{e}</span> from now on
              </label>
            ))}
          </div>
        )}

        <label className="block">
          <div className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Reason <span className="normal-case tracking-normal text-gray-600">(optional)</span></div>
          <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. District office calendar invite" maxLength={200} data-testid="dismiss-reason" />
        </label>
      </div>
    </Modal>
  );
}
